import { ZERO_WEI } from '@kwenta/sdk/constants'
import { floorNumber, formatNumber } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import { type FC, memo, useCallback, useMemo } from 'react'

import TextButton from 'components/Button/TextButton'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import NumericInput from 'components/Input/NumericInput'
import { getStep } from 'components/Slider/Slider'
import StyledSlider from 'components/Slider/StyledSlider'
import Spacer from 'components/Spacer'
import { selectShowPositionModal } from 'state/app/selectors'
import { selectEditPositionInputs } from 'state/futures/common/selectors'
import { editSmartMarginPositionMargin } from 'state/futures/isolatedMargin/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'

type OrderSizingProps = {
	isMobile?: boolean
	maxUsdInput: string
	type: 'deposit' | 'withdraw'
}

const EditPositionMarginInput: FC<OrderSizingProps> = memo(({ isMobile, type, maxUsdInput }) => {
	const dispatch = useAppDispatch()

	const { marginDelta } = useAppSelector(selectEditPositionInputs)
	const positionModal = useAppSelector(selectShowPositionModal)

	const onChangeMargin = useCallback(
		(value: string) => {
			if (positionModal?.marketAsset) {
				dispatch(editSmartMarginPositionMargin(type === 'deposit' || !value ? value : `-${value}`))
			}
		},
		[dispatch, type, positionModal?.marketAsset]
	)

	const handleSetMax = useCallback(() => {
		onChangeMargin(String(floorNumber(maxUsdInput)))
	}, [onChangeMargin, maxUsdInput])

	const onChangeValue = useCallback((_: any, v: string) => onChangeMargin(v), [onChangeMargin])
	const onChangeSlider = useCallback(
		(_: any, v: number | number[]) => onChangeMargin(String(v)),
		[onChangeMargin]
	)

	const marginDeltaWei = useMemo(() => {
		return !marginDelta || Number.isNaN(Number(marginDelta)) ? ZERO_WEI : wei(marginDelta)
	}, [marginDelta])

	const invalid = useMemo(
		() => wei(marginDeltaWei || 0).gt(maxUsdInput),
		[marginDeltaWei, maxUsdInput]
	)

	const maxUsdInputNumber = useMemo(() => wei(maxUsdInput).toNumber(), [maxUsdInput])

	return (
		<div>
			<InputHeaderRow
				label={type === 'deposit' ? 'Amount to deposit' : 'Amount to withdraw'}
				rightElement={<TextButton onClick={handleSetMax}>Max</TextButton>}
			/>

			<NumericInput
				invalid={invalid}
				dataTestId={`edit-position-size-input${isMobile ? '-mobile' : '-desktop'}`}
				value={marginDelta.replace('-', '')}
				placeholder="0.00"
				onChange={onChangeValue}
			/>
			<Spacer height={10} />
			<StyledSlider
				minValue={0}
				maxValue={maxUsdInputNumber}
				step={getStep(maxUsdInputNumber)}
				defaultValue={0}
				value={Math.abs(Number(marginDelta))}
				onChange={onChangeSlider}
				valueLabelFormat={(v: number) => formatNumber(v)}
				$currentMark={Number(marginDelta ?? 0)}
				marks={[
					{
						value: 0,
						label: '0',
					},
					{
						value: maxUsdInputNumber * 0.25,
						label: '25%',
					},
					{
						value: maxUsdInputNumber * 0.5,
						label: '50%',
					},
					{
						value: maxUsdInputNumber * 0.75,
						label: '75%',
					},
					{
						value: maxUsdInputNumber,
						label: '100%',
					},
				]}
			/>
		</div>
	)
})

export default EditPositionMarginInput
